import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Sparkline } from "@/components/Sparkline";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { api, type AdminOrgEnriched } from "@/lib/api";
import { formatCost, formatDate, relativeIso } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const columns: ColumnDef<AdminOrgEnriched>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-2">
        {row.original.name}
        {row.original.status && row.original.status !== "active" && (
          <Badge variant="outline" className="text-[10px] uppercase">
            {row.original.status}
          </Badge>
        )}
      </span>
    ),
  },
  {
    accessorKey: "slug",
    header: "Slug",
    cell: ({ getValue }) => {
      const slug = getValue() as string | null;
      return slug ? (
        <span className="font-mono text-xs text-muted-foreground">{slug}</span>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      );
    },
  },
  {
    accessorKey: "ownerName",
    header: "Owner",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">
        {(getValue() as string) ?? "\u2014"}
      </span>
    ),
  },
  {
    accessorKey: "ownerEmail",
    header: "Owner email",
    cell: ({ getValue }) => {
      const email = getValue() as string | null;
      return email ? (
        <span className="text-muted-foreground">{email}</span>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      );
    },
  },
  {
    accessorKey: "memberCount",
    header: "Members",
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue() as number}</span>
    ),
  },
  {
    accessorKey: "containerCount",
    header: "Containers",
    cell: ({ getValue }) => (
      <span className="tabular-nums">{getValue() as number}</span>
    ),
  },
  {
    accessorKey: "creditBalance",
    header: "Credit Balance",
    cell: ({ getValue }) => {
      const balance = getValue() as number;
      return (
        <span className={`tabular-nums ${balance < 1 ? "text-destructive" : ""}`}>
          {formatCost(balance, "USD")}
        </span>
      );
    },
  },
  {
    accessorKey: "lifetimeSpend",
    header: "Lifetime Spend",
    cell: ({ getValue }) => (
      <span className="tabular-nums">{formatCost(getValue() as number, "USD")}</span>
    ),
  },
  {
    accessorKey: "lifetimeGranted",
    header: "Lifetime Granted",
    cell: ({ getValue }) => (
      <span className="tabular-nums text-muted-foreground">
        {formatCost(getValue() as number, "USD")}
      </span>
    ),
  },
  {
    id: "consumption",
    header: "Consumption (30d)",
    enableSorting: false,
    cell: ({ row }) => (
      <Sparkline
        data={row.original.consumptionSparkline ?? []}
        className="text-primary"
      />
    ),
  },
  {
    accessorKey: "lastActivityAt",
    header: "Last Activity",
    cell: ({ getValue }) => {
      const iso = getValue() as string | null;
      return (
        <span className="text-muted-foreground whitespace-nowrap">
          {relativeIso(iso)}
        </span>
      );
    },
    sortingFn: (a, b) => {
      const av = a.original.lastActivityAt ? new Date(a.original.lastActivityAt).getTime() : 0;
      const bv = b.original.lastActivityAt ? new Date(b.original.lastActivityAt).getTime() : 0;
      return av - bv;
    },
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ getValue }) => formatDate(getValue() as string),
  },
];

export function AdminOrgsPage() {
  useDocumentTitle("Admin: Organizations");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data: listResponse, isLoading, isError, error } = useQuery({
    queryKey: ["admin-organizations-enriched"],
    queryFn: api.admin.orgsEnriched,
  });
  const data = listResponse?.data;

  // Client-side filter across the columns that identify an org, so the search
  // box matches what a super-admin would actually type: name, slug, owner,
  // owner email.
  const filtered = useMemo(() => {
    if (!data) return data;
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((o) =>
      [o.name, o.slug, o.ownerName, o.ownerEmail]
        .filter((v): v is string => typeof v === "string")
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [data, search]);

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Organizations"
        subtitle="All organizations across your platform. Click a row to view details."
      />

      {isLoading ? (
        <LoadingIndicator />
      ) : isError || !data ? (
        <ErrorComponent error={error ?? "Failed to load organizations"} />
      ) : (
        <div className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, slug, or owner…"
              className="pl-8"
            />
          </div>
          <DataTable
            columns={columns}
            data={filtered ?? []}
            isLoading={false}
            enableSorting
            initialSorting={[{ id: "lastActivityAt", desc: true }]}
            onRowClick={(row) => navigate({ to: "/superadmin/organizations/$orgId", params: { orgId: row.id } })}
          />
        </div>
      )}
    </div>
  );
}
